/**
 * FareLadder — the compact ladder of fare levels under a flight row.
 *
 * Calm by default: a row of small chips (level + price, plus Miles). Hover on a
 * mouse, or a tap, opens ONE in-flow panel directly beneath the chips with what
 * that level includes and "What you'll pay". The panel is in the page flow, not
 * a floating layer, so it cannot be clipped or mis-placed on a phone.
 *
 * Renders nothing when the flight has fewer than two fare levels and no member
 * price, so a flight we know little about looks exactly as it always did.
 */
import { useEffect, useRef, useState } from "react";
import { Check, Dot, X } from "lucide-react";
import FareHistoryToggle from "./FareHistoryToggle";
import type { PlanFare } from "@/lib/fareOptionsTypes";
import type { PlanFlight } from "@/lib/types";
import {
  awardView,
  compareToHeadline,
  extraPriceText,
  headlineFare,
  includeLines,
  ladderChips,
  memberLine,
  milesShort,
  money,
  safeHref,
  shortLevel,
  sourceText,
  yourPriceBreakdown,
  type LadderChip,
} from "./fareLadderView";

interface FareLadderProps {
  flight: PlanFlight;
  /** What the row's one price includes ("Main · 1 bag"); shown at the end of the chip line on wider screens. */
  priceLabel?: string | null;
  /** Open this chip's panel on first render (tests, deep links). */
  defaultOpenKey?: string | null;
  /** With both, the ladder offers this flight's price history by fare type. */
  tripId?: string;
  routeId?: string;
}

interface OpenState {
  key: string;
  /** Pinned by a tap or click; unpinned panels close when the pointer leaves. */
  pinned: boolean;
}

const HOVER_DELAY_MS = 140;

function SourceLink({ url, date }: { url: string; date: string }) {
  const href = safeHref(url);
  const label = sourceText(date);
  if (!href) return <span className="text-muted-foreground">{label}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
    >
      {label}
    </a>
  );
}

function IncludesList({ fare }: { fare: PlanFare }) {
  const lines = includeLines(fare);
  if (lines.length === 0) {
    return <p className="text-muted-foreground">The airline did not list what this level includes.</p>;
  }
  return (
    <ul className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
      {lines.map((l) => (
        <li key={l.key} className="flex items-start gap-1.5">
          {l.state === "yes" ? (
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden="true" />
          ) : l.state === "no" ? (
            <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <Dot className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <span className="text-foreground">{l.text}</span>
        </li>
      ))}
    </ul>
  );
}

/** "What you'll pay": the fare, every extra with its price and advice, and the traveller's own total. */
function Breakdown({ flight, fares }: { flight: PlanFlight; fares: PlanFare[] }) {
  const yp = flight.yourPrice ?? null;
  const extras = flight.extras ?? [];
  if (!yp && extras.length === 0) return null;
  const head = headlineFare(fares);
  const ypFare = yp ? fares.find((f) => f.level === yp.fareLevel) : null;
  const view = yp ? yourPriceBreakdown(yp, ypFare ? ypFare.price : null) : null;
  const inPrice = new Set((yp?.extrasIncluded ?? []).map((e) => e.kind));
  return (
    <section aria-label="What you'll pay" className="border-t pt-2">
      <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">What you'll pay</h4>
      {view && (
        <div className="mb-2 rounded-md bg-muted/60 px-2 py-1.5">
          <ul className="space-y-0.5">
            {view.lines.map((l) => (
              <li key={l.key} className="flex justify-between gap-3">
                <span className="text-foreground">{l.label}</span>
                <span
                  className={
                    "tabular-nums " + (l.amount === "priced at booking" ? "text-muted-foreground" : "font-mono text-foreground")
                  }
                >
                  {l.amount}
                </span>
              </li>
            ))}
            {view.promo && (
              <li className="text-emerald-700 dark:text-emerald-400">{view.promo[0].toUpperCase() + view.promo.slice(1)}</li>
            )}
          </ul>
          <p className="mt-1 flex justify-between gap-3 border-t pt-1 font-semibold text-foreground">
            <span>Your price</span>
            <span className="font-mono tabular-nums">{view.total}</span>
          </p>
        </div>
      )}
      {!view && head && (
        <p className="mb-1 flex justify-between gap-3">
          <span className="text-foreground">{shortLevel(head.level)} fare</span>
          <span className="font-mono tabular-nums text-foreground">{money(head.price)}</span>
        </p>
      )}
      {extras.length > 0 && (
        <ul className="space-y-1.5">
          {extras.map((e) => (
            <li key={e.kind + e.name}>
              <p className="flex flex-wrap justify-between gap-x-3">
                <span className="font-medium text-foreground">
                  {e.name}
                  {inPrice.has(e.kind) && (
                    <span className="ml-1.5 font-normal text-[hsl(var(--accent-foreground))]">in your price</span>
                  )}
                </span>
                <span className={e.price == null ? "text-muted-foreground" : "tabular-nums text-foreground"}>
                  {extraPriceText(e)}
                </span>
              </p>
              <p className="text-muted-foreground">
                {e.advice && <>{e.advice} </>}
                <SourceLink url={e.sourceUrl} date={e.checkedAt} />
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FarePanel({ fare, flight, fares }: { fare: PlanFare; flight: PlanFlight; fares: PlanFare[] }) {
  return (
    <div className="space-y-2">
      <p className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="text-sm font-semibold text-foreground">{fare.level}</span>
        <span className="font-mono text-sm font-semibold tabular-nums text-foreground">{money(fare.price)}</span>
      </p>
      <p className="text-muted-foreground">{compareToHeadline(fare, headlineFare(fares))}</p>
      <IncludesList fare={fare} />
      <Breakdown flight={flight} fares={fares} />
    </div>
  );
}

function MilesPanel({ flight }: { flight: PlanFlight }) {
  const award = flight.award;
  if (!award) return null;
  const v = awardView(award);
  return (
    <div className="space-y-1.5">
      <p className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="text-sm font-semibold text-foreground">{award.program} miles</span>
        <span className="font-mono text-sm font-semibold tabular-nums text-foreground">{v.price}</span>
      </p>
      <p className="text-foreground">{v.value}</p>
      <p className={award.verdict === "use_miles" ? "font-medium text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}>
        {v.verdict}
      </p>
      {award.seatsAvailable != null && (
        <p className="text-muted-foreground">
          {award.seatsAvailable} {award.seatsAvailable === 1 ? "seat" : "seats"} left at this price.
        </p>
      )}
    </div>
  );
}

function MorePanel({ chip, fares }: { chip: Extract<LadderChip, { kind: "more" }>; fares: PlanFare[] }) {
  const head = headlineFare(fares);
  return (
    <ul className="space-y-1.5">
      {chip.hidden.map((f) => (
        <li key={f.level}>
          <p className="flex justify-between gap-3">
            <span className="font-medium text-foreground">{f.level}</span>
            <span className="font-mono tabular-nums text-foreground">{money(f.price)}</span>
          </p>
          <p className="text-muted-foreground">{compareToHeadline(f, head)}</p>
        </li>
      ))}
    </ul>
  );
}

export default function FareLadder({ flight, priceLabel = null, defaultOpenKey = null, tripId, routeId }: FareLadderProps) {
  const chips = ladderChips(flight);
  const member = flight.memberPrice ? memberLine(flight.memberPrice) : null;
  const [open, setOpen] = useState<OpenState | null>(
    defaultOpenKey ? { key: defaultOpenKey, pinned: true } : null,
  );
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clear, []);

  if (chips.length === 0 && !member && !priceLabel) return null;

  const panelId = "fare-panel-" + flight.flightId;
  const active = open ? chips.find((c) => c.key === open.key) ?? null : null;
  const fares = flight.fares ?? [];

  return (
    <div
      className="pb-1.5 pl-2 pr-2 sm:pl-12"
      onPointerLeave={() => {
        clear();
        setOpen((o) => (o && !o.pinned ? null : o));
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(null);
      }}
    >
      {(chips.length > 0 || priceLabel) && (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div role="group" aria-label="Fare levels" className="flex flex-wrap items-center gap-1.5">
          {chips.map((c) => {
            const selected = open?.key === c.key;
            const base =
              "inline-flex min-h-[28px] items-baseline gap-1 rounded-full border px-2 py-1 text-[11px] leading-none transition-colors motion-reduce:transition-none sm:min-h-[22px] sm:py-0.5 ";
            const tone =
              c.kind === "miles" && c.useMiles
                ? "border-emerald-600 dark:border-emerald-400 "
                : c.kind === "fare" && c.yours
                  ? "border-[hsl(var(--primary))]/50 bg-accent "
                  : "border-border ";
            return (
              <button
                key={c.key}
                type="button"
                aria-expanded={selected}
                aria-controls={panelId}
                className={base + tone + (selected ? "bg-muted " : "hover:bg-muted/60 ")}
                onPointerEnter={(e) => {
                  if (e.pointerType !== "mouse") return;
                  clear();
                  timer.current = setTimeout(
                    () => setOpen((o) => (o?.pinned ? o : { key: c.key, pinned: false })),
                    HOVER_DELAY_MS,
                  );
                }}
                onClick={() => {
                  clear();
                  setOpen((o) => (o && o.key === c.key && o.pinned ? null : { key: c.key, pinned: true }));
                }}
              >
                {c.kind === "fare" && (
                  <>
                    <span className="text-muted-foreground">{c.label}</span>
                    <span className="font-mono font-medium tabular-nums text-foreground">{money(c.price)}</span>
                  </>
                )}
                {c.kind === "miles" && (
                  <>
                    <span className="text-muted-foreground">Miles</span>
                    <span className="font-mono font-medium tabular-nums text-foreground">{milesShort(c.miles)}</span>
                    {c.useMiles && <span className="sr-only">, worth using</span>}
                  </>
                )}
                {c.kind === "more" && (
                  <>
                    <span className="font-medium text-muted-foreground">{c.label}</span>
                    <span className="sr-only">more fare levels</span>
                  </>
                )}
              </button>
            );
          })}
        </div>
        {priceLabel && (
          <span className="hidden whitespace-nowrap text-[10px] text-muted-foreground sm:inline">Your price: {priceLabel}</span>
        )}
        </div>
      )}

      {active && (
        <div
          id={panelId}
          role="region"
          aria-label="Fare detail"
          className="mt-1.5 rounded-md border bg-card px-3 py-2 text-xs text-card-foreground shadow-paper"
        >
          {active.kind === "fare" && <FarePanel fare={active.fare} flight={flight} fares={fares} />}
          {active.kind === "miles" && <MilesPanel flight={flight} />}
          {active.kind === "more" && <MorePanel chip={active} fares={fares} />}
        </div>
      )}

      {tripId && routeId && flight.dedupeKey && chips.length > 0 && (
        <FareHistoryToggle tripId={tripId} routeId={routeId} dedupeKey={flight.dedupeKey} />
      )}

      {member && (
        <p
          className={
            "mt-1 text-[11px] " +
            (member.tone === "good" ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground")
          }
        >
          {member.text}
        </p>
      )}
    </div>
  );
}
